/**
 * /settings/team — manager/admin/owner only.
 *
 * Iter 231.6 — Surfaces FULL member admin inline (no popup overlays):
 * profile (name/phone/avatar), login email, personal/HR emails, role,
 * password reset, and active/inactive status. Click any row → expands
 * the <MemberAdminPanel>. Replaces the legacy per-row popup editors
 * that only let you edit a handful of fields.
 */
import { useEffect, useState } from "react";
import {
  Plus, ChevronDown, ChevronRight, Shield,
} from "lucide-react";
import api, { formatErr } from "../../lib/api";
import Modal from "../../components/Modal";
import { useAuth } from "../../context/AuthContext";
import { EntityShell } from "../../components/shell";
import Avatar from "../../components/Avatar";
import { PasswordResetRequestsBlock } from "./_blocks";
import MemberAdminPanel from "../../components/team/MemberAdminPanel";
import UserPermissionsSection from "../../components/team/UserPermissionsSection";

const empty = { email: "", password: "", name: "", role: "technician", phone: "" };

export default function TeamSettings() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [err, setErr] = useState("");
  const [openRowId, setOpenRowId] = useState(null);

  const load = async () => {
    const u = await api.get("/users").catch(() => ({ data: [] }));
    setUsers(u.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const payload = { ...form };
      if (!payload.password || !payload.password.trim()) delete payload.password;
      await api.post("/auth/register", payload);
      setOpen(false); setForm(empty); load();
    } catch (e2) { setErr(formatErr(e2.response?.data?.detail)); }
  };

  const isManager = user?.role === "owner" || user?.role === "admin" || user?.role === "manager";

  if (!isManager) {
    return (
      <EntityShell kindLabel="Settings" title="Team" backTo="/settings">
        <div className="hv-card p-5 text-sm text-[#a0aabf]" data-testid="team-settings-forbidden">
          Manager access required.
        </div>
      </EntityShell>
    );
  }

  return (
    <EntityShell
      kindLabel="Settings"
      title="Team & roles"
      backTo="/settings"
      actions={["admin", "owner"].includes(user?.role) ? (
        <button onClick={() => setOpen(true)} className="hv-btn hv-btn-primary text-xs" data-testid="user-new-btn">
          <Plus className="w-3.5 h-3.5" /> Invite user
        </button>
      ) : null}
    >
      <div className="space-y-4" data-testid="settings-team-page">
        <PasswordResetRequestsBlock actorRole={user?.role} />

        <div className="hv-card p-5" data-testid="team-card">
          <h2 className="font-display text-2xl font-bold flex items-center gap-2 mb-1">
            <Shield className="w-5 h-5 text-[#1fb6c1]" /> Users
          </h2>
          <p className="text-xs text-[#a0aabf] mb-4">
            Click any member to manage profile, login email, personal emails,
            role, password and access. Owner accounts can only be edited by
            the Owner themselves.
          </p>

          <div className="divide-y divide-white/5">
            {users.map((u) => {
              const isOwner = u.role === "owner";
              const isSelf = u.id === user?.id;
              const expanded = openRowId === u.id;
              // Owner rows are read-only for everyone except the owner themselves.
              const canExpand = !(isOwner && !isSelf);
              return (
                <div key={u.id} data-testid={`user-row-${u.id}`}>
                  <button
                    type="button"
                    onClick={() => canExpand && setOpenRowId(expanded ? null : u.id)}
                    disabled={!canExpand}
                    className={`w-full flex items-center gap-3 py-3 text-left ${canExpand ? "hover:bg-white/[0.02] cursor-pointer" : "cursor-default opacity-80"}`}
                    data-testid={`user-row-toggle-${u.id}`}
                  >
                    {canExpand && (expanded
                      ? <ChevronDown className="w-4 h-4 text-[#647087]" />
                      : <ChevronRight className="w-4 h-4 text-[#647087]" />
                    )}
                    {!canExpand && <span className="w-4 h-4 inline-block" />}
                    <Avatar user={u} size="sm" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium truncate">{u.name || u.email}</span>
                        {isOwner && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-amber-400/40 text-amber-300 bg-amber-400/5">
                            Owner
                          </span>
                        )}
                        {isSelf && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#A78BFA]/40 text-[#A78BFA] bg-[#A78BFA]/5">
                            You
                          </span>
                        )}
                        {u.is_active === false && (
                          <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300 bg-rose-500/5">
                            Deactivated
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-[#647087] font-mono truncate">
                        {u.email} · {u.role}
                      </div>
                    </div>
                  </button>

                  {expanded && canExpand && (
                    <div className="pb-4 pl-7 pr-2" data-testid={`user-row-expanded-${u.id}`}>
                      <MemberAdminPanel
                        user={u}
                        meRole={user?.role}
                        meId={user?.id}
                        onChanged={load}
                        permissionsSlot={
                          <UserPermissionsSection user={u} meRole={user?.role} onChanged={load} />
                        }
                      />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Invite user modal — kept as Modal since this is a terminal "create
          a new entity" flow; the no-modals rule covers confirmations, not
          creation forms. */}
      <Modal open={open} onClose={() => setOpen(false)} title="Invite user" testId="user-modal">
        <form onSubmit={submit} className="space-y-3" data-testid="user-form">
          {[
            ["name", "Name *", "text", true],
            ["email", "Email *", "email", true],
            ["phone", "Phone", "text", false],
          ].map(([k, l, t, r]) => (
            <div key={k}>
              <label className="text-xs uppercase tracking-wider text-[#a0aabf]">{l}</label>
              <input type={t} required={r} value={form[k]}
                onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                className="hv-input mt-1" data-testid={`user-input-${k}`} />
            </div>
          ))}
          <div>
            <label className="text-xs uppercase tracking-wider text-[#a0aabf]">Role</label>
            <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}
              className="hv-input mt-1" data-testid="user-input-role">
              <option value="technician">Technician</option>
              <option value="manager">Manager</option>
              <option value="admin">Admin</option>
              <option value="client">Client</option>
            </select>
          </div>
          <details className="group">
            <summary className="text-xs text-[#647087] cursor-pointer hover:text-white select-none list-none flex items-center gap-1">
              <span className="group-open:rotate-90 transition-transform">›</span>
              Set an initial password (optional)
            </summary>
            <div className="mt-2">
              <input type="password" value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="hv-input" data-testid="user-input-password"
                placeholder="Leave blank — user will set on first login" />
            </div>
          </details>
          {err && <div className="text-sm text-rose-300">{err}</div>}
          <button type="submit" className="hv-btn hv-btn-primary w-full" data-testid="user-submit">
            Create user
          </button>
        </form>
      </Modal>
    </EntityShell>
  );
}
